import React, { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { apiFetch } from '../lib/api';

/**
 * MaintenanceBanner — affiche un bandeau global quand le backend portail est
 * en cours de redémarrage (déploiement, restart, etc.).
 *
 * Detection : api-client.js émet un CustomEvent('api-status-change') quand
 * apiFetch détecte ≥ 2 erreurs 5xx / network consécutives dans 30s. Le bandeau
 * s'affiche jusqu'à ce qu'une requête API réussisse (status='online').
 *
 * Le composant est branché globalement dans App.js (rendu unique au-dessus
 * du <Sidebar /> + <Outlet />).
 */
// Durée au-delà de laquelle une indisponibilité n'est plus un simple redémarrage
// de déploiement (qui rend la main bien avant).
const RESTART_GRACE_SEC = 180;

export default function MaintenanceBanner() {
  const [status, setStatus] = useState('online'); // 'online' | 'maintenance'
  const [shownAt, setShownAt] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  // Écoute les events du wrapper apiFetch
  useEffect(() => {
    function handleStatus(ev) {
      const next = ev?.detail?.status;
      if (next === 'maintenance' || next === 'online') {
        setStatus(next);
        if (next === 'maintenance') {
          setShownAt(Date.now());
        } else {
          setShownAt(null);
          setElapsedSec(0);
        }
      }
    }
    window.addEventListener('api-status-change', handleStatus);
    return () => window.removeEventListener('api-status-change', handleStatus);
  }, []);

  // Compteur de secondes écoulées (utile pour rassurer "ça fait Xs")
  useEffect(() => {
    if (status !== 'maintenance' || !shownAt) return;
    const t = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - shownAt) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [status, shownAt]);

  // Le bandeau PROMET une « reconnexion automatique » — encore fallait-il que
  // quelque chose reconnecte. Il ne se désarmait que si une requête partait
  // d'elle-même, donc si l'utilisateur cliquait. Sur un écran qui ne sonde rien,
  // il restait collé indéfiniment alors que le service était revenu : le bandeau
  // survivait à sa cause. Constat 49 du registre #20266 (son autre volet, le
  // libellé qui affirmait « Redémarrage du service », est corrigé plus bas).
  //
  // apiFetch appelle `_recordApiSuccess` dès qu'une requête aboutit, ce qui émet
  // `api-status-change` avec status 'online' : le bandeau se retire alors seul.
  // La sonde ne tourne QUE bandeau armé, donc quand le service est déjà supposé
  // muet — elle n'ajoute aucune charge en fonctionnement normal.
  useEffect(() => {
    if (status !== 'maintenance') return;
    const t = setInterval(() => {
      apiFetch('/api/health').catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [status]);

  if (status !== 'maintenance') return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
        color: '#451a03',
        padding: '10px 20px',
        fontFamily: "'IBM Plex Sans', sans-serif",
        fontSize: '0.9rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        animation: 'slideDown 0.3s ease-out',
      }}
    >
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
        @keyframes spin360 {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .mtnce-spin { animation: spin360 1.5s linear infinite; }
      `}</style>
      <AlertTriangle size={20} />
      <span>
        <RefreshCw size={14} className="mtnce-spin" style={{ verticalAlign: 'middle', marginRight: 6 }} />
        {/* Ce bandeau s'arme sur 2 erreurs 5xx ou réseau en 30 s (_recordApiError).
            Dans l'immense majorité des cas c'est un déploiement : l'annoncer comme
            une panne (« Le service ne répond pas ») fait passer un geste VOLONTAIRE
            pour un bug. On annonce donc le redémarrage — mais seulement tant que
            c'est plausible : un déploiement rend la main en quelques dizaines de
            secondes. Au-delà de RESTART_GRACE_SEC, ce n'en est plus un (le 03/09,
            le conteneur n'avait pas redémarré) et le libellé redevient l'état
            constaté. (registre #20266, 52 — arbitrage William 22/09) */}
        {elapsedSec < RESTART_GRACE_SEC
          ? <b>Redémarrage du service en cours</b>
          : <b>Le service met plus de temps que prévu à redémarrer</b>}
        {' '}— reconnexion automatique
        {elapsedSec > 5 && (
          <span style={{ marginLeft: 8, fontWeight: 400, opacity: 0.8 }}>
            (depuis {elapsedSec}s)
          </span>
        )}
      </span>
    </div>
  );
}
